import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../config/firebase";
import EmergencyProperty from "../../types/emergency_propert";

class EmergencyPropertyService {
  constructor() {
    this.collectionName = "emergency_properties";
  }

  // Create
  async createEmergencyProperty(propertyData) {
    try {
      const property = new EmergencyProperty(propertyData);
      const docRef = await addDoc(
        collection(db, this.collectionName),
        property.toObject()
      );
      return docRef.id;
    } catch (error) {
      throw new Error(`Failed to create emergency property: ${error.message}`);
    }
  }

  // Read - Get all properties
  async getAllEmergencyProperties() {
    try {
      const q = query(
        collection(db, this.collectionName),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) =>
        EmergencyProperty.fromFirestore(doc)
      );
    } catch (error) {
      throw new Error(`Failed to get emergency properties: ${error.message}`);
    }
  }

  // Read - Get single property by ID
  async getEmergencyPropertyById(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return EmergencyProperty.fromFirestore(docSnap);
      }
      return null;
    } catch (error) {
      throw new Error(`Failed to get emergency property: ${error.message}`);
    }
  }

  // Update
  async updateEmergencyProperty(id, propertyData) {
    try {
      const property = new EmergencyProperty({ ...propertyData, id });
      const docRef = doc(db, this.collectionName, id);
      await updateDoc(docRef, property.toObject());
    } catch (error) {
      throw new Error(`Failed to update emergency property: ${error.message}`);
    }
  }

  // Delete
  async deleteEmergencyProperty(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      throw new Error(`Failed to delete emergency property: ${error.message}`);
    }
  }

  // Real-time listener for all properties
  subscribeToEmergencyProperties(callback) {
    const q = query(
      collection(db, this.collectionName),
      orderBy("createdAt", "desc")
    );

    return onSnapshot(q, (querySnapshot) => {
      const properties = querySnapshot.docs.map((doc) =>
        EmergencyProperty.fromFirestore(doc)
      );
      callback(properties);
    });
  }

  // Search by urgency level
  async getPropertiesByUrgencyLevel(urgencyLevel) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where("urgencyLevel", "==", urgencyLevel),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) =>
        EmergencyProperty.fromFirestore(doc)
      );
    } catch (error) {
      throw new Error(
        `Failed to get properties by urgency level: ${error.message}`
      );
    }
  }

  // Search by property type
  async getPropertiesByType(propertyType) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where("propertyType", "==", propertyType),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) =>
        EmergencyProperty.fromFirestore(doc)
      );
    } catch (error) {
      throw new Error(`Failed to get properties by type: ${error.message}`);
    }
  }

  // Real-time listener with filters
  subscribeToFilteredProperties(filters, callback) {
    let q = collection(db, this.collectionName);

    // Add filters if provided
    const constraints = [];
    if (filters.urgencyLevel) {
      constraints.push(where("urgencyLevel", "==", filters.urgencyLevel));
    }
    if (filters.propertyType) {
      constraints.push(where("propertyType", "==", filters.propertyType));
    }

    constraints.push(orderBy("createdAt", "desc"));
    q = query(q, ...constraints);

    return onSnapshot(q, (querySnapshot) => {
      const properties = querySnapshot.docs.map((doc) =>
        EmergencyProperty.fromFirestore(doc)
      );
      callback(properties);
    });
  }
}

// Create and export a singleton instance
const emergencyPropertyService = new EmergencyPropertyService();
export default emergencyPropertyService;
